import {IWixAPI} from '@wix/yoshi-flow-editor';
import {IViewerScriptExports} from '@wix/wixstores-client-storefront-sdk/dist/es/src/viewer-script/wixcode/createWixcodeExports';
import {origin, STORES_APP_DEF_ID} from '../../../constants/app';
import {getSlugFromPlatform} from '../services/product-service/getSlugFromPlatform';
import {setSeoTags} from '../services/product-service/setSeoTags';
import {handleInteractions} from '../services/widget/handleInteractions';
import {createInitialState} from './createInitialState';
import {fetchDeferredData} from './data/fetchDeferredData';
import {bindWidgetsProps} from '../services/widget/bindWidgetsProps';
import {RootWidgetControllerParams} from '../config/types';
import {RootStoreState} from './RootStoreState';
import {createReportPanoramaTransaction, ReportPanoramaTransactionFn} from './createReportPanoramaTransaction';
import {createServices, RootServices} from './createServices';
import {getEcomPublicApi, getStoresPublicApi} from './getPublicApis';
import {ILocaleKeys} from '../../../locale-keys/LocaleKeys';
import {initLocaleKeys} from '../../../services/platform-service/initLocaleKeys';
import {ExpressCheckoutStore} from './ExpressCheckoutStore';
import {EcomHttpClient} from '@wix/wixstores-client-storefront-sdk/dist/es/src/viewer-script/site-store/EcomHttpClient';
import {getEcomHttpClient} from '@wix/wixstores-client-storefront-sdk/dist/es/src/viewer-script/site-store/editor-flow/getEcomHttpClient';
import {publishRelatedProductsEvent} from '../services/product-service/publishRelatedProductsEvent';
import {RootWidgetApiService} from '../services/rootWidgetApiService';
import {waitUntilReadyFactory} from '../../../utils/utils';
import {publishPageLoadedTrackEvent} from '../services/product-service/publishPageLoadedTrackEvent';
import {publishTrackViewContentTrackEvent} from '../services/product-service/publishTrackViewContentTrackEvent';
import {SPECS} from '../../../specs';
import {setReviewsProps} from '../services/widget/widget-props/setReviewsProps';
import {autorun} from 'mobx';
import {IOnlineStoresViewerScript} from '@wix/wixstores-client-storefront-sdk';

export class RootStore {
  public $state!: RootStoreState;
  public origin = origin;
  public services: RootServices;
  public t: ILocaleKeys;
  public wixCodeApi: IWixAPI;
  public ecomPublicApi: IViewerScriptExports;
  public storesPublicApi: IOnlineStoresViewerScript['exports'];
  public reportPanoramaTransaction: ReportPanoramaTransactionFn;
  public expressCheckoutStore: ExpressCheckoutStore;
  public ecomHttpClient: EcomHttpClient;
  public readonly breadcrumbsInitialItems: $w.Breadcrumbs.Item[];
  public wixCodeService: RootWidgetApiService;
  public waitForLocationChangedReady: Promise<void>;

  constructor(public readonly controllerParams: RootWidgetControllerParams) {
    this.wixCodeApi = this.controllerParams.controllerConfig.wixCodeApi;
    this.t = initLocaleKeys(this.controllerParams.flowAPI.translations.i18n);
    this.reportPanoramaTransaction = createReportPanoramaTransaction(this);
    this.services = createServices(this);
    this.ecomHttpClient = getEcomHttpClient({
      flowAPI: this.controllerParams.flowAPI as any,
      wixCodeApi: this.wixCodeApi as any,
    });
    try {
      this.breadcrumbsInitialItems = [...this.services.components.breadcrumbs.items];
    } catch (_e) {
      //
    }
    handleInteractions(this);
  }

  private async initProduct({slug, shouldFetchDeferredOnInit}: {slug?: string; shouldFetchDeferredOnInit: boolean}) {
    const {onReadyResolver, waitUntilReady} = waitUntilReadyFactory();

    this.waitForLocationChangedReady = waitUntilReady;
    const initialState = await createInitialState(this, slug ?? (getSlugFromPlatform(this) as string), {
      shouldFetchDeferredOnInit,
    });
    if (initialState) {
      this.$state = initialState;
      this.wixCodeService = new RootWidgetApiService(this);
      bindWidgetsProps(this);
      setSeoTags(this);
      publishRelatedProductsEvent(this);
      publishPageLoadedTrackEvent(this);
      publishTrackViewContentTrackEvent(this);
    }

    const productId = initialState?.product.id;
    if (productId && this.isExperimentEnabled(SPECS.ProductPageSsrInvalidationTags)) {
      try {
        const publicApi = await this.controllerParams.controllerConfig.wixCodeApi.site.getPublicAPI(STORES_APP_DEF_ID);
        publicApi.ssrCache.setCustomInvalidationTag([`product_${productId}`]);
      } catch {
        // Silently ignore SSR cache invalidation errors to avoid breaking product initialization
      }
    }

    autorun(() => setReviewsProps(this));
    if (initialState) {
      await onReadyResolver('');
    }
    if (this.isExperimentEnabled(SPECS.UpdateBreadcrumbsAfterHydration) && !shouldFetchDeferredOnInit) {
      void this.syncDeferredData();
    }
  }
  public async init(): Promise<void> {
    this.ecomPublicApi = await getEcomPublicApi(this);
    if (this.isExperimentEnabled(SPECS.AddToCartUseStoresViewerScript)) {
      this.storesPublicApi = await getStoresPublicApi(this);
    }
    await this.initProduct({shouldFetchDeferredOnInit: false});
    this.wixCodeApi.location.onChange(() => void this.initProduct({shouldFetchDeferredOnInit: true}));
    this.controllerParams.$widget.onPropsChanged(
      (_, {slug}) => void this.initProduct({slug, shouldFetchDeferredOnInit: true}),
    );
  }

  public async syncDeferredData() {
    if (this.$state && !this.controllerParams.flowAPI.environment.isSSR) {
      const {referringPage} = await fetchDeferredData(this, this.$state);
      this.$state.setReferringPage(referringPage);
    }
  }

  public isExperimentEnabled(experimentKey: string) {
    return this.controllerParams.flowAPI.experiments.enabled(experimentKey);
  }
}
